import * as React from 'react'
import { cn } from '@/lib/utils'
import {
  Check,
  CheckCheck,
  Clock,
  AlertCircle,
  Image as ImageIcon,
  FileText,
  Mic,
  Play,
  Pause,
  Bot,
  Download,
} from 'lucide-react'
import type { WAMessage, WAMessageStatus } from '@/lib/api-client'

interface ChatBubbleProps {
  message: WAMessage
  showDate?: boolean
  isFirstInGroup?: boolean
  isLastInGroup?: boolean
}

export function ChatBubble({ message, showDate, isFirstInGroup = true, isLastInGroup = true }: ChatBubbleProps) {
  const isOutgoing = message.direction === 'outgoing'
  const isAi = message.isAiGenerated

  // Format time
  const time = new Date(message.createdAt).toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })

  const dateLabel = showDate ? formatDateLabel(new Date(message.createdAt)) : null
  const content = getMessageContent(message)

  // Tail logic: first message in group gets a sharp corner (tail)
  const getBubbleRadius = () => {
    if (isFirstInGroup) {
      return isOutgoing
        ? 'rounded-md rounded-tr-[2px]'
        : 'rounded-md rounded-tl-[2px]'
    }
    return 'rounded-md'
  }

  return (
    <>
      {/* Date separator */}
      {dateLabel && (
        <div className="flex justify-center my-3">
          <span className="px-3 py-1.5 rounded-lg bg-white text-[#54656f] text-[11px] font-medium shadow-sm">
            {dateLabel}
          </span>
        </div>
      )}

      {/* Message bubble */}
      <div className={cn('flex', isOutgoing ? 'justify-end' : 'justify-start')}>
        <div
          className={cn(
            'relative max-w-[65%] px-[9px] py-[6px] pb-[8px] shadow-sm group',
            getBubbleRadius(),
            isOutgoing
              ? 'bg-[#d9fdd3] text-[#111b21]'
              : 'bg-white text-[#111b21]'
          )}
        >
          {/* AI badge */}
          {isAi && (
            <div className={cn(
              'flex items-center gap-1 mb-1 text-[10px] font-medium',
              isOutgoing ? 'text-[#00a884]' : 'text-[#00a884]'
            )}>
              <Bot className="w-3 h-3" />
              <span>AI Auto-Reply</span>
            </div>
          )}

          {/* Text message */}
          {content.type === 'text' && (
            <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap break-words">
              {content.text}
            </p>
          )}

          {/* Image message */}
          {content.type === 'image' && (
            <div className="space-y-1.5">
              {content.imageUrl ? (
                <div className="relative group/img">
                  <img
                    src={content.imageUrl}
                    alt="Photo"
                    className="max-w-full rounded-md max-h-[280px] object-cover"
                    loading="lazy"
                    crossOrigin="use-credentials"
                  />
                  <a
                    href={`${content.imageUrl}?download=1`}
                    download="image"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/50 flex items-center justify-center opacity-0 group-hover/img:opacity-100 transition-opacity duration-150 text-white hover:bg-black/70"
                  >
                    <Download className="w-3.5 h-3.5" />
                  </a>
                </div>
              ) : (
                <div className={cn(
                  'flex items-center gap-2 p-4 rounded-md',
                  isOutgoing ? 'bg-[#c6f0c0]' : 'bg-[#f0f2f5]'
                )}>
                  <ImageIcon className="w-6 h-6 opacity-60" />
                  <span className="text-sm opacity-80">Photo</span>
                </div>
              )}
              {content.caption && (
                <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap break-words">
                  {content.caption}
                </p>
              )}
            </div>
          )}

          {/* Document message */}
          {content.type === 'document' && (
            <div className={cn(
              'flex items-center gap-3 p-2.5 rounded-md',
              isOutgoing ? 'bg-[#c6f0c0]' : 'bg-[#f0f2f5]'
            )}>
              <div className={cn(
                'w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0',
                isOutgoing ? 'bg-[#00a884]/15' : 'bg-[#00a884]/10'
              )}>
                <FileText className={cn('w-5 h-5', isOutgoing ? 'text-[#00a884]' : 'text-[#00a884]')} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-[13px] font-medium truncate">
                  {content.fileName || 'Document'}
                </p>
                {content.caption && (
                  <p className="text-[11px] text-[#667781] truncate mt-0.5">{content.caption}</p>
                )}
              </div>
              {content.documentUrl && (
                <a
                  href={`${content.documentUrl}?download=1`}
                  download={content.fileName || 'document'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors hover:bg-black/5 text-[#667781] hover:text-[#111b21]"
                >
                  <Download className="w-4 h-4" />
                </a>
              )}
            </div>
          )}

          {/* Audio message */}
          {content.type === 'audio' && (
            <AudioPlayer
              mediaId={message.mediaId}
              isOutgoing={isOutgoing}
            />
          )}

          {/* Template message */}
          {content.type === 'template' && (
            <div className="space-y-1">
              <div className={cn(
                'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium',
                isOutgoing ? 'bg-[#00a884]/20 text-[#00a884]' : 'bg-[#00a884]/10 text-[#00a884]'
              )}>
                Template
              </div>
              {content.imageUrl && (
                <img
                  src={content.imageUrl}
                  alt="Template header"
                  className="max-w-full rounded-md max-h-[280px] object-cover"
                  loading="lazy"
                  crossOrigin="use-credentials"
                />
              )}
              {content.text && (
                <p className="text-[14.2px] leading-[19px] whitespace-pre-wrap break-words">
                  {content.text}
                </p>
              )}
            </div>
          )}

          {/* Footer: Time + Status */}
          <div className={cn(
            'flex items-center justify-end gap-1 mt-0.5 -mb-[2px]',
            isOutgoing ? 'text-[#667781]' : 'text-[#667781]'
          )}>
            <span className="text-[11px]">{time}</span>
            {isOutgoing && <MessageStatusIcon status={message.status} />}
          </div>
        </div>
      </div>
    </>
  )
}

// Audio player component
function AudioPlayer({ mediaId, isOutgoing }: { mediaId?: string | null; isOutgoing: boolean }) {
  const audioRef = React.useRef<HTMLAudioElement>(null)
  const [isPlaying, setIsPlaying] = React.useState(false)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [duration, setDuration] = React.useState(0)

  const apiBase = import.meta.env.VITE_API_URL || ''
  const audioSrc = mediaId ? `${apiBase}/api/v1/wa/conversations/media/${mediaId}` : undefined

  const togglePlay = () => {
    if (!audioRef.current) return
    if (isPlaying) {
      audioRef.current.pause()
    } else {
      audioRef.current.play()
    }
  }

  const formatTime = (s: number) => {
    const mins = Math.floor(s / 60)
    const secs = Math.floor(s % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0

  return (
    <div className={cn(
      'flex items-center gap-2.5 p-2.5 rounded-md min-w-[200px]',
      isOutgoing ? 'bg-[#c6f0c0]' : 'bg-[#f0f2f5]'
    )}>
      {audioSrc && (
        <audio
          ref={audioRef}
          src={audioSrc}
          preload="metadata"
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onEnded={() => { setIsPlaying(false); setCurrentTime(0) }}
          onTimeUpdate={() => setCurrentTime(audioRef.current?.currentTime ?? 0)}
          onLoadedMetadata={() => setDuration(audioRef.current?.duration ?? 0)}
        />
      )}
      <button
        type="button"
        onClick={togglePlay}
        disabled={!audioSrc}
        className={cn(
          'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors',
          isOutgoing
            ? 'bg-white/20 hover:bg-white/30 text-white'
            : 'bg-[#00a884]/10 hover:bg-[#00a884]/20 text-[#00a884]'
        )}
      >
        {isPlaying
          ? <Pause className="w-4 h-4" />
          : <Play className="w-4 h-4 ml-0.5" />
        }
      </button>
      <div className="flex-1 flex flex-col gap-1">
        <div className="w-full h-1 rounded-full bg-current opacity-20 overflow-hidden">
          <div
            className={cn(
              'h-full rounded-full transition-all',
              isOutgoing ? 'bg-white' : 'bg-[#00a884]'
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
        <span className="text-[11px] opacity-60">
          {isPlaying || currentTime > 0 ? formatTime(currentTime) : formatTime(duration)}
        </span>
      </div>
      <Mic className="w-4 h-4 opacity-40 flex-shrink-0" />
    </div>
  )
}

// Message status icon
function MessageStatusIcon({ status }: { status: WAMessageStatus }) {
  switch (status) {
    case 'pending':
      return <Clock className="w-3 h-3 opacity-70" />
    case 'sent':
      return <Check className="w-3 h-3" />
    case 'delivered':
      return <CheckCheck className="w-3 h-3" />
    case 'read':
      return <CheckCheck className="w-3.5 h-3.5 text-[#53bdeb]" />

    case 'failed':
      return <AlertCircle className="w-3 h-3 text-red-300" />
    default:
      return null
  }
}

// Helper functions

function formatDateLabel(date: Date): string {
  const today = new Date()
  const yesterday = new Date(today)
  yesterday.setDate(yesterday.getDate() - 1)

  if (date.toDateString() === today.toDateString()) {
    return 'Today'
  }
  if (date.toDateString() === yesterday.toDateString()) {
    return 'Yesterday'
  }

  return date.toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'long',
    year: date.getFullYear() !== today.getFullYear() ? 'numeric' : undefined,
  })
}

interface MessageContent {
  type: 'text' | 'image' | 'document' | 'audio' | 'template' | 'unknown'
  text?: string
  imageUrl?: string
  documentUrl?: string
  fileName?: string
  caption?: string
  templateName?: string
}

function getMessageContent(message: WAMessage): MessageContent {
  const { messageType, textBody, mediaUrl, mediaFileName, templateName, messageContent } = message

  let parsed: Record<string, unknown> | null = null
  if (messageContent) {
    try {
      parsed = JSON.parse(messageContent)
    } catch {
      // Ignore parse errors
    }
  }

  switch (messageType) {
    case 'text':
      return { type: 'text', text: textBody || '' }

    case 'image': {
      const apiBase = import.meta.env.VITE_API_URL || ''
      // Use stored mediaUrl, or build proxy URL from mediaId, or from parsed content
      const imgMediaId = message.mediaId || (parsed?.mediaId as string | undefined)
      const imgUrl = mediaUrl
        || (imgMediaId ? `${apiBase}/api/v1/wa/conversations/media/${imgMediaId}` : undefined)
      return {
        type: 'image',
        imageUrl: imgUrl,
        caption: textBody || (parsed?.caption as string | undefined),
      }
    }

    case 'document': {
      const apiBase = import.meta.env.VITE_API_URL || ''
      const docMediaId = message.mediaId || (parsed?.mediaId as string | undefined)
      const docUrl = mediaUrl
        || (docMediaId ? `${apiBase}/api/v1/wa/conversations/media/${docMediaId}` : undefined)
      return {
        type: 'document',
        documentUrl: docUrl,
        fileName: mediaFileName || (parsed?.fileName as string | undefined),
        caption: parsed?.caption as string | undefined,
      }
    }

    case 'audio':
      return { type: 'audio' }

    case 'template': {
      const apiBase = import.meta.env.VITE_API_URL || ''
      // Render the header image (campaign poster) so staff have visual context
      // for the reply. Prefer stored mediaUrl, else proxy the Meta media id.
      const tplMediaId = message.mediaId || (parsed?.mediaId as string | undefined)
      const tplImageUrl = mediaUrl
        || (tplMediaId ? `${apiBase}/api/v1/wa/conversations/media/${tplMediaId}` : undefined)
      return {
        type: 'template',
        templateName: templateName || (parsed?.templateName as string | undefined) || 'Unknown',
        text: textBody || undefined,
        imageUrl: tplImageUrl,
      }
    }

    default:
      return { type: 'text', text: textBody || `[${messageType}]` }
  }
}
